import {createAsyncThunk} from '@reduxjs/toolkit'

import {ROUTER_ORDER_DELIVERY_TRACKING} from '@web/core/routes'
import {changeBrowserLocation} from '@web/helpers/browser'
import {urlMergedWithUnverifiedUrlParam} from '@web/helpers/path'

import {LocationChangeHistoryWithRouteParam, NavigationResult, ThunkApi} from '../interfaces'

import navigateToRoute from './navigateToRoute'

interface NavigateDeliveryTrackingUrlParams extends LocationChangeHistoryWithRouteParam {
    url: string
}

interface NavigateDeliveryTrackingSeqIdsParams extends LocationChangeHistoryWithRouteParam {
    seqIds: {
        productOrderNo: string
        deliveryNo: string
    }
    queries?: {
        closeType?: 'close' | 'back'
        user?: 'customer' | 'seller'
    }
}

const shouldParseWithUrl = (
    params: NavigateDeliveryTrackingUrlParams | NavigateDeliveryTrackingSeqIdsParams,
): params is NavigateDeliveryTrackingUrlParams => {
    return 'url' in params && typeof params.url === 'string'
}

const shouldParseWithSeqIds = (
    params: NavigateDeliveryTrackingUrlParams | NavigateDeliveryTrackingSeqIdsParams,
): params is NavigateDeliveryTrackingSeqIdsParams => {
    return 'seqIds' in params && typeof params.seqIds === 'object'
}

type NavigateToCustomServiceParams = NavigateDeliveryTrackingUrlParams | NavigateDeliveryTrackingSeqIdsParams

/**
 * 배송조회 페이지로 이동.
 */
const navigateToDeliveryTracking = createAsyncThunk<NavigationResult, NavigateToCustomServiceParams, ThunkApi>(
    'navigateTo/deliveryTracking',
    async (params, {dispatch}) => {
        const {routeBased = true, historyType = 'PUSH', ...urlOrSeqIds} = params

        const {productOrderNo, deliveryNo, closeType, user} = (() => {
            if (shouldParseWithUrl(urlOrSeqIds)) {
                const match = urlOrSeqIds.url.match(/\/tracking\/(\d+)\/(\d+)/)
                const rawCloseTypeQuery = urlOrSeqIds.url.match(/[?&]closeType=([^&]+)/)?.[1]
                const rawUserQuery = urlOrSeqIds.url.match(/[?&]user=([^&]+)/)?.[1]

                return {
                    productOrderNo: match?.[1],
                    deliveryNo: match?.[2],
                    ...(rawCloseTypeQuery && {closeType: rawCloseTypeQuery}),
                    ...(rawUserQuery && {user: rawUserQuery}),
                }
            } else if (shouldParseWithSeqIds(urlOrSeqIds)) {
                return {
                    productOrderNo: urlOrSeqIds.seqIds.productOrderNo,
                    deliveryNo: urlOrSeqIds.seqIds.deliveryNo,
                    ...(urlOrSeqIds.queries?.closeType && {closeType: urlOrSeqIds.queries.closeType}),
                    ...(urlOrSeqIds.queries?.user && {user: urlOrSeqIds.queries.user}),
                }
            }
            return {
                productOrderNo: '',
                deliveryNo: '',
            }
        })()

        if (!productOrderNo || !deliveryNo) {
            return Promise.reject(new Error('Product order number or delivery number is missing.'))
        }

        if (routeBased) {
            dispatch(
                navigateToRoute({
                    historyType,
                    path: urlMergedWithUnverifiedUrlParam(
                        ROUTER_ORDER_DELIVERY_TRACKING.ROOT.getPath(`/${productOrderNo}/${deliveryNo}`),
                        {closeType, user},
                    ),
                    resetScroll: true,
                }),
            )
        } else {
            changeBrowserLocation({
                type: historyType,
                url: urlMergedWithUnverifiedUrlParam(
                    ROUTER_ORDER_DELIVERY_TRACKING.ROOT.getHref(`/${productOrderNo}/${deliveryNo}`),
                    {closeType, user},
                ),
            })
        }

        return Promise.resolve()
    },
)

export default navigateToDeliveryTracking
